import { useCallback, useMemo } from 'react'

import { Card, SkeletonBox, DescriptionList } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { useModal } from '@circlefin/modal-router'
import { paymentMethodsSection } from '@services/sections/lib/billing'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  RemoveMerchantCreditCardDocument,
  SetMerchantCreditCardDocument,
  useCardPaymentMethodByIdQuery,
  useRemoveMerchantCreditCardMutation,
  useSetMerchantCreditCardMutation,
} from '@shared/graphql'
import { useMfa } from '@shared/mfa'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import * as Buttons from '../../../buttons'

const { Label, Description } = DescriptionList

const schema = y.object({
  /**
   * Expiration date.
   */
  expiry: y.string().required().expiry(),
  /**
   * Cvv.
   */
  cvv: y.string().required().min(3).max(3),
})

export type UpdateCardFormData = y.InferType<typeof schema>

export interface CardDetailProps {
  /**
   * Merchant card ID for card details.
   */
  cardId: string
}

export const CardDetail = ({ cardId }: CardDetailProps) => {
  const { t } = useTranslation('settings')

  const [withMfa, { context, mfaError, mfaSuccess }] = useMfa()
  const modal = useModal()
  const router = useRouter()

  const { data, loading, error, refetch } = useCardPaymentMethodByIdQuery({
    variables: {
      id: cardId,
    },
  })

  const cardDetails = data?.cardPaymentMethodById

  const defaultValues = useMemo(() => {
    const expMonth = cardDetails?.expMonth
      ? cardDetails.expMonth.padStart(2, '0')
      : '0'

    const expYear = cardDetails?.expYear ? cardDetails?.expYear : '0'

    return {
      expiry: `${expMonth}/${expYear}`,
      cvv: '',
    }
  }, [cardDetails?.expMonth, cardDetails?.expYear])

  const [Form] = useForm<UpdateCardFormData>({
    schema,
    defaultValues,
  })

  const [setCardDetails] = useSetMerchantCreditCardMutation({
    refetchQueries: [SetMerchantCreditCardDocument],
    awaitRefetchQueries: true,
    onCompleted: () => {
      mfaSuccess({
        message: t`common:success`,
        description: t`invoices.billing.linkedCards.setCard`,
      })
    },
    onError: (error) => {
      mfaError({ error })
    },
  })

  const [removeCard] = useRemoveMerchantCreditCardMutation({
    refetchQueries: [RemoveMerchantCreditCardDocument],
    onCompleted: () => {
      mfaSuccess({
        message: t`common:success`,
        description: cardDetails?.isDefault
          ? t`invoices.billing.linkedCards.deleteDefaultCard`
          : t`invoices.billing.linkedCards.deleteCard`,
      })
      void router.push(paymentMethodsSection.route)
    },
    onError: (error) => {
      mfaError({ error })
    },
  })

  const handleSetCard = useCallback(
    (cardId: string, values: UpdateCardFormData) => {
      const expMonth = values.expiry?.slice(0, 2)
      const expYear = values.expiry?.slice(3, 7)

      if (expMonth != null && expYear != null) {
        withMfa({
          onComplete: (code) => {
            void setCardDetails({
              variables: {
                setMerchantCreditCardInput: {
                  cardId,
                  cvv: values.cvv,
                  expMonth,
                  expYear,
                },
              },
              context: context(code),
            })
          },
          onAbort: () => modal.close({ context: 'onDismiss' }),
        })
      }
    },
    [withMfa, setCardDetails, modal, context],
  )

  const handleRemoveCard = useCallback(() => {
    if (cardDetails) {
      withMfa({
        onComplete: (code) => {
          void removeCard({
            variables: {
              id: cardDetails.id,
            },
            context: context(code),
          })
        },
        onAbort: () => modal.close({ context: 'onDismiss' }),
      })
    }
  }, [withMfa, context, cardDetails, removeCard, modal])

  const changeCardExpiry: React.ChangeEventHandler<HTMLInputElement> =
    useCallback((e) => {
      if (e.target.value.length === 2) {
        e.target.value = `${e.target.value}/`
      }
    }, [])

  const onSubmit = useCallback(
    (values: UpdateCardFormData) => {
      if (cardDetails) {
        handleSetCard(cardDetails.id, values)
      }
    },
    [cardDetails, handleSetCard],
  )

  return (
    <>
      <Form className="space-y-8" onSubmit={onSubmit}>
        <Card>
          <Card.Content className="pb-8 pl-8 pt-5">
            <GraphQLErrorBoundary error={error} retry={refetch}>
              <div
                className=" px-4 pb-7 pt-4 text-lg leading-6 font-circular-book"
                data-testid="card-details-title"
              >
                {t('billing.cardPayment.details.title')}
              </div>
              <DescriptionList className="ml-4 grid-cols-2">
                <Label className="w-64">
                  {t('billing.cardPayment.details.cardNumber.label')}
                </Label>
                <Description>
                  {t('billing.cardPayment.details.cardNumber.value', {
                    network: cardDetails?.network ?? '',
                    last4: cardDetails?.last4 ?? '',
                  })}
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.details.cvv')}
                </Label>
                <Description>
                  <Form.Input className="w-48" maxLength={3} name="cvv" />
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.details.expiry')}
                </Label>
                <Description>
                  <Form.Input
                    className="w-48"
                    maxLength={7}
                    name="expiry"
                    onChange={changeCardExpiry}
                  />
                </Description>
              </DescriptionList>
            </GraphQLErrorBoundary>
          </Card.Content>
        </Card>
        <Card>
          <Card.Content className="pb-8 pl-8 pt-5">
            <GraphQLErrorBoundary error={error} retry={refetch}>
              <div
                className="px-4 pb-7 pt-4 text-lg leading-6 font-circular-book"
                data-testid="card-holderDetails-title"
              >
                {t('billing.cardPayment.holderDetails.title')}
              </div>
              <DescriptionList className="ml-4">
                <Label className="w-64">
                  {' '}
                  {t('billing.cardPayment.holderDetails.fullName')}
                </Label>
                <Description>
                  <SkeletonBox
                    className="h-8 w-64"
                    data-testid="billing-details-name"
                    loading={loading}
                  >
                    {cardDetails?.billingDetails.name}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.email')}
                </Label>
                <Description>
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.metadata?.email}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.addressLine1')}{' '}
                </Label>
                <Description>
                  {' '}
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.billingDetails.line1}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.addressLine2')}
                </Label>
                <Description>
                  {' '}
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.billingDetails.line2}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.country')}
                </Label>
                <Description>
                  {' '}
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.billingDetails.country}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.city')}
                </Label>
                <Description>
                  {' '}
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.billingDetails.city}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.state')}
                </Label>
                <Description>
                  {' '}
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.billingDetails.district}
                  </SkeletonBox>
                </Description>

                <Label className="w-64">
                  {t('billing.cardPayment.holderDetails.postalCode')}
                </Label>
                <Description>
                  {' '}
                  <SkeletonBox className="h-8 w-64" loading={loading}>
                    {cardDetails?.billingDetails.postalCode}
                  </SkeletonBox>
                </Description>
              </DescriptionList>
            </GraphQLErrorBoundary>
          </Card.Content>
        </Card>
        {!error && (
          <div className="flex justify-end space-x-8 p-6">
            <Buttons.BillingDashboard
              className="w-28"
              onClick={handleRemoveCard}
              variant="text"
            >
              {t('common:delete')}
            </Buttons.BillingDashboard>
            <Buttons.Forms.UpdateCard className="w-28" variant="primary">
              {t('common:update')}
            </Buttons.Forms.UpdateCard>
          </div>
        )}
      </Form>
    </>
  )
}
